import { useState, useEffect } from 'react';
import { unitService, buildingService, propertyService } from 'services/api';
import SearchableSelect from 'components/SearchableSelect';
import PropertyCombobox from 'components/PropertyCombobox';
import { useAuth } from 'context/AuthContext';
import { ComunicadosHistorialModal } from 'components/ComunicadosPanel';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import GrupoEncabezado from 'components/GrupoEncabezado';
import AsambleaEnCursoAviso from 'components/AsambleaEnCursoAviso';
import UnitFormModal, { TIPO_ICON } from './UnitFormModal';
import Paginacion from 'components/Paginacion';
import styles from './Units.module.css';

const POR_PAGINA = 50;

// Vista del propietario en celular: una tarjeta por unidad (la tabla de 6 columnas
// obligaba a deslizar de lado para ver el coeficiente y el botón Historial).
function UnidadTarjeta({ u, onHistorial }) {
  const pid = u.building?.property?.id || u.building?.property_id;
  const pname = u.building?.property?.name || '—';
  const fila = (etiqueta, valor) => (
    <div className={styles.tarjetaFila}>
      <span className={styles.tarjetaEtiqueta}>{etiqueta}</span>
      <span className={styles.tarjetaValor}>{valor}</span>
    </div>
  );
  return (
    <li className={styles.tarjeta}>
      <div className={styles.tarjetaCabecera}>
        <strong className={styles.tarjetaTitulo}>Unidad {u.numero_unidad}</strong>
        <span className={`badge badge-secondary ${styles.capitalizar}`}>{TIPO_ICON[u.tipo_inmueble]} {u.tipo_inmueble || '—'}</span>
      </div>
      {fila('Torre', u.building?.numero_torre || '—')}
      {fila('Conjunto', pname)}
      {fila('Coeficiente', <span className={styles.coef}>{parseFloat(u.coefficiente_votacion || 0).toFixed(4)}</span>)}
      <button className={`btn btn-secondary ${styles.botonHistorial}`} disabled={!pid}
        title={`Comunicados del conjunto ${pname}`}
        onClick={() => onHistorial({ propertyId: pid, propertyName: pname })}>
        <i className="fas fa-bullhorn" /> Historial de comunicados
      </button>
    </li>
  );
}

export default function Units() {
  const { isAdmin, isSuperAdmin, user, getSubscriptionStateForProperty } = useAuth();
  const isUser           = !isAdmin;
  const isAdminMultiProp = user?.role === 'admin';

  const [items,      setItems]     = useState([]);
  const [pagina,     setPagina]    = useState(1);
  const [totalUnid,  setTotalUnid]  = useState(0);   // de TODA la torre (el servidor pagina)
  const [coefTorre,   setCoefTorre]   = useState(0);   // suma de coeficientes de TODA la torre
  const [loading,    setLoading]   = useState(false);
  const [alert,      setAlert]     = useState(null);
  const [show,       setShow]      = useState(false);
  const [editing,    setEditing]   = useState(null);
  const [properties,       setProperties]       = useState([]);
  const [buildings,        setBuildings]        = useState([]);
  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [filterProp,      setFilterProp]      = useState('');
  const [filterBuilding,  setFilterBuilding]  = useState('');
  const [selectedPropObj, setSelectedPropObj] = useState(null);
  const [propsLoaded,     setPropsLoaded]     = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [showComunicados, setShowComunicados] = useState(null); // null | { propertyId?, propertyName? }
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting,   setDeleting]   = useState(false);
  const toggleBuilding = (id) => setCollapsed(c => ({ ...c, [id]: !c[id] }));

  // Estado de suscripción
  const effectiveSub = selectedPropObj ? getSubscriptionStateForProperty(selectedPropObj) : 'none';
  const isReadOnly   = effectiveSub === 'grace';
  const isBlocked    = effectiveSub === 'blocked';

  // Usuario normal: cargar sus unidades
  useEffect(() => {
    if (isUser) { setLoading(true); unitService.getAll().then(r => { setItems(Array.isArray(r.data) ? r.data : (r.data?.data || [])); setLoading(false); }).catch(() => setLoading(false)); }
  }, [isUser]);

  // Cargar propiedades para admin
  useEffect(() => {
    if (isAdminMultiProp || isSuperAdmin) {
      propertyService.selector({ limit: 200 }).then(r => {
        const props = r.data.data || [];
        setProperties(props);
        setPropsLoaded(true);
        if (isAdminMultiProp && !isSuperAdmin && props.length === 1) {
          // Auto-seleccionar la única propiedad y cargar sus torres
          setFilterProp(String(props[0].id));
          setSelectedPropObj(props[0]);
          setLoadingBuildings(true);
          buildingService.getAll({ property_id: props[0].id, limit: 200 })
            .then(r2 => { setBuildings(r2.data.data || []); setLoadingBuildings(false); })
            .catch(() => setLoadingBuildings(false));
        }
      });
    }
  }, [isAdminMultiProp, isSuperAdmin]);

  const handleFilterProp = (propId, elegida) => {
    const propObj = elegida || properties.find(p => String(p.id) === String(propId)) || null;
    setFilterProp(propId); setSelectedPropObj(propObj);
    setFilterBuilding(''); setBuildings([]); setItems([]);
    if (!propId) return;
    setLoadingBuildings(true);
    buildingService.getAll({ property_id: propId, limit: 200 })
      .then(r => { setBuildings(r.data.data || []); setLoadingBuildings(false); })
      .catch(() => setLoadingBuildings(false));
  };

  const handleFilterBuilding = (buildingId, p = 1) => {
    setFilterBuilding(buildingId); setItems([]); setPagina(p);
    if (!buildingId) { setTotalUnid(0); setCoefTorre(0); return; }
    setLoading(true);
    unitService.getAll({ building_id: buildingId, page: p, limit: POR_PAGINA })
      .then(r => {
        const d = r.data;
        // Si al borrar quedó vacía la última página, se vuelve a la última que existe.
        if (!Array.isArray(d) && p > 1 && d?.lastPage >= 1 && p > d.lastPage) return handleFilterBuilding(buildingId, d.lastPage);
        setItems(Array.isArray(d) ? d : (d?.data || []));
        setTotalUnid(Array.isArray(d) ? d.length : (d?.total || 0));
        setCoefTorre(Number(d?.totalCoef) || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const irAPagina = (n) => { handleFilterBuilding(filterBuilding, n); window.scrollTo({ top: 0 }); };

  const reload = () => { if (filterBuilding) handleFilterBuilding(filterBuilding, pagina); };

  const del = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await unitService.delete(confirmDel.id);
      setAlert({ type: 'success', message: 'Unidad eliminada' });
      setConfirmDel(null);
      reload();
    } catch (err) { setAlert({ type: 'danger', message: err.response?.data?.message || 'Error al eliminar' }); setConfirmDel(null); }
    finally { setDeleting(false); }
  };

  const openEdit = (u) => { setEditing(u); setShow(true); };

  const selectedPropName     = selectedPropObj?.name || '';
  const selectedBuildingName = filterBuilding ? buildings.find(b => String(b.id) === String(filterBuilding))?.numero_torre || '' : '';
  const loadingProps     = isAdminMultiProp && !isSuperAdmin && !propsLoaded;
  const needsPropSelect  = propsLoaded && (isSuperAdmin || (isAdminMultiProp && properties.length > 1)) && !filterProp;
  const waitingBuilding  = !isUser && !needsPropSelect && !loadingProps && propsLoaded && !filterBuilding;
  const totalCoef        = coefTorre;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1><i className={`fas fa-door-open ${styles.iconoTitulo}`} />{isUser ? 'Mis unidades' : 'Unidades'}</h1>
          <p>{isUser ? 'Unidades de las que eres propietario' : needsPropSelect ? 'Selecciona propiedad y torre' : filterBuilding ? `Unidades de ${selectedBuildingName}${selectedPropName ? ` · ${selectedPropName}` : ''}` : selectedPropName ? `Torres de ${selectedPropName} — selecciona una torre` : 'Selecciona una torre para ver sus unidades'}</p>
        </div>
        <div className={styles.accionesEncabezado}>

          {/* Propietario: historial de comunicados (todos) */}
          {isUser && (
            <button className="btn btn-secondary" onClick={() => setShowComunicados({})}>
              <i className="fas fa-bullhorn" /> Todos los comunicados
            </button>
          )}

          {/* Selector propiedad — super_admin y admin multi-prop */}
          {!isUser && (isSuperAdmin || (isAdminMultiProp && properties.length > 1)) && (
            <div className={styles.filaSelector}>
              <i className={`fas fa-building ${styles.iconoSelector}`} />
              <PropertyCombobox value={filterProp} onChange={handleFilterProp} placeholder="— Selecciona propiedad —" />
              {filterProp && !isSuperAdmin && <button className="btn btn-sm btn-secondary btn-icon" onClick={() => handleFilterProp('')} title="Limpiar"><i className="fas fa-times" /></button>}
            </div>
          )}

          {/* Selector torre — aparece cuando hay propiedad seleccionada */}
          {!isUser && !needsPropSelect && !loadingProps && propsLoaded && (
            <div className={styles.filaSelector}>
              <i className={`fas fa-layer-group ${styles.iconoSelector}`} />
              {loadingBuildings ? (
                <div className={styles.cargandoTorres}>
                  <div className={`spinner ${styles.spinnerChico}`} /> Cargando torres...
                </div>
              ) : (
                <>
                  <SearchableSelect value={filterBuilding} onChange={(id) => handleFilterBuilding(id)} placeholder="— Selecciona torre —" minWidth={160} maxWidth={220} ariaLabel="Torre"
                    options={buildings.map(b => ({ value: b.id, label: b.numero_torre }))} />
                  {filterBuilding && <button className="btn btn-sm btn-secondary btn-icon" onClick={() => handleFilterBuilding('')} title="Limpiar"><i className="fas fa-times" /></button>}
                </>
              )}
            </div>
          )}

          {/* Botón nueva unidad */}
          {isAdmin && !isReadOnly && !isBlocked && !needsPropSelect && !loadingProps && (
            <button className="btn btn-primary" disabled={!filterBuilding} title={!filterBuilding ? 'Selecciona una torre primero' : ''}
              onClick={() => { setEditing(null); setShow(true); }}>
              <i className="fas fa-plus" /> Nueva unidad
            </button>
          )}
        </div>
      </div>

      {alert && <div className={`alert alert-${alert.type} ${styles.alertaClic}`} onClick={() => setAlert(null)}>{alert.message}</div>}

      {(filterProp || !(isSuperAdmin || isAdminMultiProp)) && <AsambleaEnCursoAviso propertyId={filterProp} />}

      {/* Banners membresía */}
      {isBlocked && <div className={`${styles.banner} ${styles.bannerBloqueada}`}>
        <i className={`fas fa-ban ${styles.bannerIcono}`} /><span><strong>Membresía bloqueada.</strong> No puedes gestionar unidades de {selectedPropName} hasta renovar.</span>
      </div>}
      {isReadOnly && !isBlocked && <div className={`${styles.banner} ${styles.bannerSoloLectura}`}>
        <i className={`fas fa-lock ${styles.bannerIcono}`} /><span><strong>Solo lectura.</strong> La membresía de {selectedPropName} está en período de gracia.</span>
      </div>}

      {loadingProps && <div className="loading"><div className="spinner" /></div>}
      {needsPropSelect && <div className="card"><div className="empty-state"><i className={`fas fa-door-open ${styles.vacioIcono}`} /><p className={styles.vacioTexto}>Selecciona una propiedad para comenzar</p></div></div>}
      {waitingBuilding && !loadingBuildings && <div className="card"><div className="empty-state"><i className={`fas fa-layer-group ${styles.vacioIcono}`} /><p className={styles.vacioTexto}>{buildings.length === 0 ? `No hay torres${selectedPropName ? ` en "${selectedPropName}"` : ''}` : 'Selecciona una torre'}</p></div></div>}
      {loadingBuildings && <div className="loading"><div className="spinner" /></div>}
      {!loadingBuildings && loading && <div className="loading"><div className="spinner" /></div>}
      {!loading && !loadingBuildings && filterBuilding && items.length === 0 && <div className="card"><div className="empty-state"><i className="fas fa-door-open" /><p>Sin unidades en {selectedBuildingName || 'esta torre'}</p></div></div>}

      {/* Vista usuario */}
      {isUser && !loading && items.length > 0 && (
        <div className="card">
          <div className="card-header"><h2>Mis unidades</h2><span className="badge badge-primary">{items.length}</span></div>
          <div className="table-wrapper solo-escritorio"><table><thead><tr><th>Unidad</th><th>Torre</th><th>Conjunto</th><th>Tipo</th><th>Coeficiente</th><th>Comunicados</th></tr></thead>
            <tbody>{items.map(u => {
              const pid = u.building?.property?.id || u.building?.property_id;
              const pname = u.building?.property?.name || '—';
              return (
                <tr key={u.id}>
                  <td><strong>{u.numero_unidad}</strong></td>
                  <td>{u.building?.numero_torre || '—'}</td>
                  <td>{pname}</td>
                  <td><span className={`badge badge-secondary ${styles.capitalizar}`}>{TIPO_ICON[u.tipo_inmueble]} {u.tipo_inmueble || '—'}</span></td>
                  <td className={styles.coef}>{parseFloat(u.coefficiente_votacion || 0).toFixed(4)}</td>
                  <td>
                    <button className="btn btn-sm btn-secondary" disabled={!pid}
                      title={`Comunicados del conjunto ${pname}`}
                      onClick={() => setShowComunicados({ propertyId: pid, propertyName: pname })}>
                      <i className="fas fa-bullhorn" /> Historial
                    </button>
                  </td>
                </tr>
              );
            })}</tbody>
          </table></div>
          <ul className={`solo-celular ${styles.listaTarjetas}`} aria-label="Mis unidades">
            {items.map(u => <UnidadTarjeta key={u.id} u={u} onHistorial={setShowComunicados} />)}
          </ul>
        </div>
      )}

      {/* Vista admin */}
      {!isUser && !loading && !loadingBuildings && filterBuilding && items.length > 0 && (
        <div className={styles.tablaCaja}>
          <GrupoEncabezado
            icono="layer-group"
            titulo={selectedBuildingName}
            subtitulo={selectedPropName ? <>· {selectedPropName}</> : null}
            cifras={[{ etiqueta: 'Unidades', valor: totalUnid }, { etiqueta: 'Coef. total', valor: totalCoef.toFixed(4), mono: true }]}
            abierto={!collapsed['main']}
            onAlternar={() => toggleBuilding('main')}
          />
          {/* .table-wrapper: scroll lateral y pista de «desliza» (#334) en celular; sin él la tabla se cortaba (overflow:hidden de la caja) */}
          {!collapsed['main'] && (
            <div className="table-wrapper">
              <table className={styles.tabla}>
                <thead><tr className={styles.cabeceraFila}>
                  <th className={styles.th}>Unidad</th>
                  <th className={styles.th}>Tipo</th>
                  <th className={`${styles.th} ${styles.thDerecha}`}>Coeficiente</th>
                  <th className={styles.th}>Propietario</th>
                  {isAdmin && !isReadOnly && !isBlocked && <th className={`${styles.th} ${styles.thCentro}`}>Acciones</th>}
                </tr></thead>
                <tbody>{items.map((u, idx) => (
                  <tr key={u.id} className={`${styles.fila}${idx % 2 === 0 ? '' : ` ${styles.filaImpar}`}`}>
                    <td className={`${styles.celda} ${styles.celdaUnidad}`}>{u.numero_unidad}</td>
                    <td className={`${styles.celda} ${styles.capitalizar}`}>{TIPO_ICON[u.tipo_inmueble]} {u.tipo_inmueble || '—'}</td>
                    <td className={`${styles.celda} ${styles.celdaCoef}`}>{parseFloat(u.coefficiente_votacion || 0).toFixed(4)}</td>
                    <td className={styles.celda}>{u.owner ? <div><div className={styles.propietarioNombre}>{u.owner.nombre} {u.owner.apellido}</div><div className={styles.propietarioCorreo}>{u.owner.email}</div></div> : <span className={styles.sinPropietario}>Sin propietario</span>}</td>
                    {isAdmin && !isReadOnly && !isBlocked && <td className={`${styles.celda} ${styles.celdaCentro}`}><div className={`actions ${styles.accionesCentro}`}><button className="btn btn-sm btn-secondary btn-icon" onClick={() => openEdit(u)}><i className="fas fa-pencil" /></button><button className="btn btn-sm btn-danger btn-icon" onClick={() => setConfirmDel({ id: u.id, nombre: u.numero_unidad, torre: selectedBuildingName, propietario: u.owner ? `${u.owner.nombre} ${u.owner.apellido}` : null })}><i className="fas fa-trash" /></button></div></td>}
                  </tr>
                ))}</tbody>
                <tfoot><tr className={styles.pie}>
                  <td colSpan={2} className={`${styles.pieCelda} ${styles.pieEtiqueta}`}>Subtotal {selectedBuildingName}</td>
                  <td className={`${styles.pieCelda} ${styles.pieCoef}`}>{totalCoef.toFixed(4)}</td>
                  <td colSpan={isAdmin && !isReadOnly && !isBlocked ? 2 : 1} className={`${styles.pieCelda} ${styles.pieResumen}`}>{items.filter(u => u.owner).length}/{items.length} con propietario{totalUnid > items.length ? ' en esta página' : ''}</td>
                </tr></tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {!isUser && !loading && !loadingBuildings && filterBuilding && <Paginacion pagina={pagina} total={totalUnid} porPagina={POR_PAGINA} onCambiar={irAPagina} />}

      {/* Modal crear/editar */}
      {isAdmin && show && (
        <UnitFormModal
          editing={editing}
          buildings={buildings}
          defaultBuildingId={filterBuilding}
          onClose={() => { setShow(false); setEditing(null); }}
          onSaved={(msg) => {
            setShow(false); setEditing(null);
            setAlert({ type: 'success', message: msg });
            reload();
          }}
        />
      )}

      {showComunicados && (
        <ComunicadosHistorialModal
          propertyId={showComunicados.propertyId}
          propertyName={showComunicados.propertyName}
          onClose={() => setShowComunicados(null)}
        />
      )}

      {/* Modal confirmación eliminar */}
      {confirmDel && (
        <ConfirmDeleteModal title="Eliminar unidad" onClose={() => setConfirmDel(null)} onConfirm={del} deleting={deleting}>
          <div className={styles.eliminarPregunta}>
            ¿Eliminar la unidad <span className={styles.eliminarNombre}>{confirmDel.nombre}</span>?
          </div>
          <div className={styles.eliminarDetalle}>
            {confirmDel.torre && <><strong>{confirmDel.torre}</strong> · </>}
            {confirmDel.propietario
              ? <>Le quitará la unidad a <strong>{confirmDel.propietario}</strong> y, si era la única que tenía, su cuenta quedará desactivada. </>
              : 'Esta unidad no tiene propietario asignado. '}
            Las asistencias y votos históricos registrados en esta unidad no se borran, pero quedan sin unidad asociada.
          </div>
        </ConfirmDeleteModal>
      )}
    </div>
  );
}